/**
 * Every-item rules (workspace_definition_policy)
 *
 * What every item in this workspace must have, whatever it is being used for.
 * This is Completeness: the first of the two layers of readiness (canon 24 §3).
 * The second layer — what an item needs to be ranked in a given cycle or track —
 * is a readiness definition (readinessDefinition.ts), and it always includes the
 * Hard Stops set here.
 *
 * Each attribute or structural field can be set to:
 *   - 'optional'  → not checked; never surfaces as an issue
 *   - 'warn'      → the item shows it as missing, and nothing is blocked
 *   - 'block'     → Hard Stop: the item is incomplete until it is filled in
 *
 * Set by admins in Settings → Readiness → Every item. Loaded for every signed-in
 * person by ReadinessRulesBootstrap, so the rules apply on every screen.
 */

export type DefinitionBehavior = 'optional' | 'warn' | 'block';

export interface FieldDefinitionMeta {
  /** Stable key used in the policy record */
  fieldKey: string;
  /** Human-readable label */
  label: string;
  /** Short explanation shown in the settings UI */
  description: string;
  /** Grouping in the settings page */
  section: 'classification' | 'context' | 'relationships';
  /** Behavior applied when no override is set */
  defaultBehavior: DefinitionBehavior;
  /**
   * When true the field is always 'block' and the behavior cannot be changed.
   * Used for the title field.
   */
  alwaysBlock?: boolean;
  /**
   * When true, this field is also surfaced as a filter on the Items list,
   * so the settings page can call that out.
   */
  isFilter?: boolean;
}

/**
 * Flat map from fieldKey → behavior, as persisted in the store.
 * Custom attribute keys are also stored here alongside structural field keys.
 */
export type DefinitionPolicy = Record<string, DefinitionBehavior>;

/**
 * Canonical list of structural / contextual fields that can participate
 * in the definition policy. Custom scoring attributes are managed separately
 * via the attributesStore and merged in by the store.
 */
export const STRUCTURAL_FIELD_METAS: FieldDefinitionMeta[] = [
  {
    fieldKey: 'title',
    label: 'Title',
    description: 'Every item must have a title. This requirement cannot be removed.',
    section: 'classification',
    defaultBehavior: 'block',
    alwaysBlock: true,
  },
  {
    fieldKey: 'itemCategory',
    label: 'Item Category',
    description: 'Classifies the item type (problem, opportunity, risk, solution, commitment, investment). A track that ranks one category still requires it there.',
    section: 'classification',
    // Owner ruling 2026-10-15: missing category or altitude warns by default.
    defaultBehavior: 'warn',
    isFilter: true,
  },
  {
    fieldKey: 'altitudeLevel',
    label: 'Altitude',
    description: 'Declares the scope/level of the item. A track that ranks one altitude still requires it there.',
    section: 'classification',
    defaultBehavior: 'warn',
    isFilter: true,
  },
  {
    fieldKey: 'dedupeStatus',
    label: 'Duplicate Check',
    description: 'Confirms whether the item is unique or a known duplicate. Off by default: an item is taken to be unique unless someone marks it a duplicate.',
    section: 'classification',
    // Every new item starts 'pending', so a default of 'warn' would flag every item.
    defaultBehavior: 'optional',
    isFilter: true,
  },
  {
    fieldKey: 'metaCategory',
    label: 'Meta-Category',
    description: 'Broad ontological type (people, things, places, activities, information, time). Helps with clustering and search.',
    section: 'classification',
    defaultBehavior: 'optional',
    isFilter: true,
  },
  {
    fieldKey: 'strategicBucket',
    label: 'Strategic Bucket',
    description: 'Portfolio-level grouping (growth, commitments, reliability, platform, compliance).',
    section: 'classification',
    defaultBehavior: 'optional',
    isFilter: true,
  },
  {
    fieldKey: 'parentItemId',
    label: 'Parent Item',
    description: 'Links this item to a higher-level item. Useful for tree-based views and rollups.',
    section: 'relationships',
    defaultBehavior: 'optional',
    isFilter: false,
  },
  {
    fieldKey: 'rationale',
    label: 'Rationale (Outcome or Criteria)',
    description: 'Desired outcome or acceptance criteria. Provides the "why" behind the item.',
    section: 'context',
    defaultBehavior: 'optional',
    isFilter: false,
  },
  {
    fieldKey: 'ownerId',
    label: 'Owner',
    description: 'The person accountable for this item moving forward.',
    section: 'context',
    defaultBehavior: 'optional',
    isFilter: false,
  },
  {
    fieldKey: 'beneficiary',
    label: 'Beneficiary',
    description: 'Who benefits from this item being addressed.',
    section: 'context',
    defaultBehavior: 'optional',
    isFilter: false,
  },
  {
    fieldKey: 'tags',
    label: 'Tags',
    description: 'Free-form labels for search and custom grouping.',
    section: 'context',
    defaultBehavior: 'optional',
    isFilter: false,
  },
];
